import { useState, useEffect } from "react";
import {
  CheckCircle,
  X,
  ExternalLink,
  Star,
  Loader2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const HALAL_API_URL = import.meta.env.VITE_HALAL_API;

export default function HalalApprovalManagement() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const idToken = await user.getIdToken(true);
      const response = await fetch(
        `${HALAL_API_URL}/v1/halal/applications/pending`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to fetch applications");
      }

      const data = await response.json();

      if (data.ok && data.applications) {
        // Sort by timestamp (newest first)
        const applicationsList = [...data.applications];
        applicationsList.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setApplications(applicationsList);
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      setError("Failed to load applications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (application) => {
    if (
      !application.stripeCustomerId ||
      !application.stripePaymentMethodId
    ) {
      alert(
        "This application doesn't have payment information set up yet. Please ask the user to complete the payment setup."
      );
      return;
    }

    if (
      !window.confirm(
        `Approve application for ${
          application.restaurantData?.restaurantName
        }? This will start their ${
          application.stripeTier || "basic"
        } plan billing.`
      )
    ) {
      return;
    }

    setApproving(true);
    setError(null);

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(
        `${HALAL_API_URL}/v1/halal/application/${application.halalId}/review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            action: "approved",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to approve application");
      }

      const data = await response.json();

      if (data.ok) {
        alert(
          `Application approved successfully! Subscription ${data.subscriptionId} created.`
        );
        fetchApplications(); // Refresh the list
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error approving application:", error);
      setError(error.message || "Failed to approve application");
      alert(`Error: ${error.message || "Failed to approve application"}`);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication || !rejectionReason) return;

    setRejecting(true);
    setError(null);

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(
        `${HALAL_API_URL}/v1/halal/application/${selectedApplication.id}/review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            action: "disapproved",
            note: rejectionReason,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to reject application");
      }

      const data = await response.json();

      if (data.ok) {
        alert("Application rejected");
        setDialogOpen(false);
        setSelectedApplication(null);
        setRejectionReason("");
        fetchApplications();
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error rejecting application:", error);
      setError("Failed to reject application");
      alert("Failed to reject application");
    } finally {
      setRejecting(false);
    }
  };

  const openRejectDialog = (application) => {
    setSelectedApplication(application);
    setDialogOpen(true);
  };

  const openCertificate = (application) => {
    // Open certificate in new tab (assuming it's in Cloud Storage)
    const certificateUrl = `https://storage.googleapis.com/halal-app-staging/${application.certificateFileName}`;
    window.open(certificateUrl, "_blank");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    if (typeof timestamp === "number") {
      return new Date(timestamp).toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Halal Restaurant Approvals</h1>
          <Button
            variant="outline"
            onClick={fetchApplications}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : applications.length === 0 ? (
          <Alert>
            <AlertDescription>No pending applications</AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Restaurant</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Malware Scan</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((application) => (
                      <TableRow key={application.id}>
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold">
                              {application.restaurantData?.restaurantName ||
                                "N/A"}
                            </p>
                            {application.restaurantData?.phone && (
                              <p className="text-xs text-muted-foreground">
                                {application.restaurantData.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">
                              {application.userEmail}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {application.userId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {application.restaurantData?.city},{" "}
                          {application.restaurantData?.state}
                        </TableCell>
                        <TableCell>
                          {application.stripeTier ? (
                            <Badge
                              variant={
                                application.stripeTier === "pro"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {application.stripeTier === "pro" && (
                                <Star className="mr-1 h-3 w-3" />
                              )}
                              {application.stripeTier === "pro"
                                ? "Pro ($75)"
                                : "Basic ($25)"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50">
                              No plan
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {application.stripeCustomerId ? (
                            <Badge variant="default" className="bg-green-500">
                              Setup
                            </Badge>
                          ) : (
                            <Badge variant="destructive">Missing</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              application.malwareScan === "pass"
                                ? "default"
                                : application.malwareScan === "fail"
                                ? "destructive"
                                : "secondary"
                            }
                            className={
                              application.malwareScan === "pass"
                                ? "bg-green-500"
                                : ""
                            }
                          >
                            {application.malwareScan || "unscanned"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatDate(
                            application.createdAt || application.timestamp
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-green-600 hover:text-green-700"
                              onClick={() => handleApprove(application)}
                              disabled={
                                approving || !application.stripeCustomerId
                              }
                              title={
                                !application.stripeCustomerId
                                  ? "Payment not set up"
                                  : "Approve and start billing"
                              }
                            >
                              {approving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-700"
                              onClick={() => openRejectDialog(application)}
                              disabled={rejecting}
                              title="Reject application"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                            {application.certificateFileName && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openCertificate(application)}
                                title="View certificate"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Application</DialogTitle>
              <DialogDescription>
                Provide a clear reason for rejecting this application
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedApplication && (
                <div className="rounded-md bg-muted p-3 text-sm">
                  <p className="text-muted-foreground">
                    Restaurant:{" "}
                    <strong className="text-foreground">
                      {selectedApplication.restaurantData?.restaurantName}
                    </strong>
                  </p>
                  <p className="text-muted-foreground">
                    Owner:{" "}
                    <strong className="text-foreground">
                      {selectedApplication.userEmail}
                    </strong>
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for rejection *</Label>
                <Textarea
                  id="reason"
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a clear reason for rejection..."
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={rejecting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectionReason || rejecting}
              >
                {rejecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {rejecting ? "Rejecting..." : "Reject"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
